import { keepPreviousData } from "@tanstack/react-query"
import { type PropsWithChildren, type ReactNode, useEffect } from "react"
import { useAPIResources, useAPISessionsMeta, useAppPaginationQuery, useQueryState } from "@/hooks"
import type { TCategoryName, TSessionStats } from "@/types"
import { type IAPIParams, sessionsAPI } from "@/utils"
import { SessionsTable } from "./SessionsTable"

export function DataSessionsTable({
  head,
  children,
  fallback,
  ...params
}: PropsWithChildren<
  IAPIParams<
    | "activity_id"
    | "dtfinish"
    | "dtfinish"
    | "dtstart"
    | "dtstart"
    | "hidden"
    | "q"
    | "state"
    | "team_id"
    | "type"
    | TCategoryName
  > & { head?: ReactNode; fallback?: () => ReactNode }
>) {
  const sort = useQueryState("sort")[0]
  const paginate = useAppPaginationQuery(10)

  const { data: { data: sessions, headers } = {} } = useAPIResources(sessionsAPI, {
    limit: paginate.limit,
    offset: paginate.offset,
    sort: Array.isArray(sort) ? sort : sort ? [sort] : ["-dtstart"],
    placeholderData: keepPreviousData,
    ...params,
  })
  const { data: sessionsMeta } = useAPISessionsMeta({
    enabled: !!sessions?.length,
    where: {
      id: { $in: sessions?.map((ses) => ses.id).sort() || [] },
    },
  })

  const total = +(headers?.["x-total"] || 0)
  useEffect(() => {
    paginate.setTotal(total)
  }, [total])

  const meta = sessionsMeta?.reduce(
    (acc, ses) => {
      acc[ses.session_id] = ses
      return acc
    },
    {} as Record<string, TSessionStats>,
  )

  return (
    <SessionsTable head={head} data={sessions} fallback={fallback} meta={meta} paginate={paginate}>
      {children}
    </SessionsTable>
  )
}
